import { type ReactNode } from "react";
import { Link } from "react-router";
import { Check as CheckIcon, X, AlertTriangle, FileText } from "lucide-react";
import type {
  Check,
  Choice,
  ChoiceValue,
  DryRun,
  FiguresBlock,
  ObjectRef,
} from "@shared-core/domain/object-ui/contract";
import { formatBytes } from "@shared-core/domain/formatters";
import { filesOf } from "@shared-core/domain/object-ui/values";
import {
  pendingRoutesOfBlock,
  stillPricingSentence,
} from "@shared-core/domain/price-still-coming";
import { Checkbox } from "../../primitives/checkbox";
import { Label } from "../../primitives/label";
import { RadioGroup, RadioGroupItem } from "../../primitives/radio-group";
import { cn } from "../../primitives/utils";
import { FileLinks } from "../FileLinks";
import { useChoiceSearch } from "./ChoiceSearch";
import { EmailPreview } from "./EmailPreview";
import { MultiSelect, OptionTag } from "./MultiSelect";
import { PendingPrices } from "./PendingPrices";

function SectionTitle({ children }: { children: string }) {
  return (
    <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      {children}
    </p>
  );
}

const CHECK_ICON: Record<Check["state"], typeof CheckIcon> = {
  pass: CheckIcon,
  warn: AlertTriangle,
  fail: X,
};

const CHECK_TONE: Record<Check["state"], string> = {
  pass: "text-green-700 dark:text-green-400",
  warn: "text-amber-700 dark:text-amber-400",
  fail: "text-red-700 dark:text-red-400",
};

const CHECK_WORD: Record<Check["state"], string> = {
  pass: "Passed",
  warn: "Warning",
  fail: "Failed",
};

/**
 * What a command's dry run says, drawn the same for every command (brief
 * §9.3): display blocks (files, before → after, facts, notes, the emails it
 * will send), the choices
 * with their facts and defaults, the checks with evidence, the rows a bulk
 * command would touch, and the confirmations to tick. A custom widget placed
 * after the choices draws between them and the checks.
 */
export function DryRunBlocks({
  dryRun,
  afterChoices = null,
  placedChoiceKeys = [],
  choices,
  confirmed,
  disabled,
  hrefOf,
  onChoose,
  onChooseMany,
  searches,
  isSearchPending,
  onSearch,
  onConfirm,
  onAskAgain,
  onUseBestPriced,
}: {
  dryRun: DryRun;
  /** Drawn after the choices (a custom widget that reads them). */
  afterChoices?: ReactNode;
  /** Choices the shell draws in their field's place (their key is a declared request field), so not here. */
  placedChoiceKeys?: readonly string[];
  choices: Readonly<Record<string, ChoiceValue>>;
  confirmed: ReadonlySet<string>;
  disabled: boolean;
  /** Where a named record opens; null when the shell can't open it. */
  hrefOf: (ref: ObjectRef) => string | null;
  onChoose: (key: string, value: string) => void;
  /** A multiple choice's picks, all of them. */
  onChooseMany: (key: string, values: string[]) => void;
  /** What is typed in each searchable choice's search box, by choice key. */
  searches: Readonly<Record<string, string>>;
  /** A search change is on its way to the owner or being answered. */
  isSearchPending: boolean;
  onSearch: (key: string, text: string) => void;
  onConfirm: (key: string, isConfirmed: boolean) => void;
  /** Check again: a price the dry run was still waiting for may have come. */
  onAskAgain: () => void;
  /** Set the request-body values that choose the best route priced so far. */
  onUseBestPriced: (use: Record<string, string>) => void;
}) {
  // A route still pricing is not a failure: the pending-prices panel says it (neutral, with its wait and its "use
  // now"), so the failed check the dry run carries for Claude is not drawn as a second, red, line.
  const stillPricing = dryRun.display.find(
    (block) => block.kind === "pending_prices",
  );
  const stillPricingLabel = stillPricing
    ? stillPricingSentence(pendingRoutesOfBlock(stillPricing))
    : null;
  const checks = dryRun.checks.filter(
    (check) => check.label !== stillPricingLabel,
  );

  const refLink = (ref: ObjectRef | null, label: string) => {
    const href = ref ? hrefOf(ref) : null;
    return href ? (
      <Link to={href} className="text-primary underline">
        {label}
      </Link>
    ) : null;
  };

  return (
    <div className="space-y-4">
      {dryRun.display.map((block, n) => {
        switch (block.kind) {
          // Figures read what the choices picked, so they are drawn under them.
          case "figures":
            return null;
          case "file":
            return (
              <div key={n} className="flex items-center gap-2 text-sm">
                <FileText
                  className="size-4 text-muted-foreground"
                  aria-hidden
                />
                {/* An API path (a quote's PDF) is fetched with the person's sign-in, as a record's file is: a plain
                    link would reach the API without it. */}
                {filesOf(block).length > 0 ? (
                  <FileLinks files={filesOf(block)} />
                ) : (
                  <>
                    {block.href ? (
                      <a href={block.href} className="text-primary underline">
                        {block.name}
                      </a>
                    ) : (
                      <span>{block.name}</span>
                    )}
                    {block.size_bytes != null && (
                      <span className="text-xs text-muted-foreground">
                        {formatBytes(block.size_bytes)}
                      </span>
                    )}
                  </>
                )}
              </div>
            );
          case "before_after":
            return (
              <div key={n}>
                <SectionTitle>{block.label ?? "Before → after"}</SectionTitle>
                <div className="grid grid-cols-[minmax(0,1fr)_1.5rem_minmax(0,1fr)] items-start gap-2 text-sm">
                  <div className="rounded-md bg-muted/50 px-3 py-2">
                    {block.before.map((line) => (
                      <p key={line}>{line}</p>
                    ))}
                  </div>
                  <span aria-label="becomes" className="pt-2 text-center">
                    →
                  </span>
                  <div className="rounded-md bg-muted/50 px-3 py-2">
                    {block.after.map((line) => (
                      <p key={line}>{line}</p>
                    ))}
                  </div>
                </div>
              </div>
            );
          case "facts":
            return (
              <div key={n}>
                {block.title && <SectionTitle>{block.title}</SectionTitle>}
                <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1 text-sm">
                  {block.rows.map(([label, value]) => (
                    <div key={label} className="contents">
                      <dt className="text-muted-foreground">{label}</dt>
                      <dd className="text-right tabular-nums">{value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            );
          case "note":
            return (
              <div key={n} className="space-y-0.5">
                {block.lines.map((line) => (
                  <p key={line} className="text-xs text-muted-foreground">
                    {line}
                  </p>
                ))}
              </div>
            );
          case "email":
            return <EmailPreview key={n} block={block} />;
          case "quote":
            // Outsider words, or One's summary of them: drawn as a quote, never as One's own note.
            return (
              <figure key={n} className="text-sm">
                <figcaption className="text-xs text-muted-foreground">
                  {block.label}
                </figcaption>
                <blockquote className="mt-1 whitespace-pre-line border-l-2 border-border pl-3">
                  {block.text}
                </blockquote>
              </figure>
            );
          case "pending_prices":
            return (
              <PendingPrices
                key={n}
                block={block}
                disabled={disabled}
                onAskAgain={onAskAgain}
                onUseBest={onUseBestPriced}
              />
            );
          default:
            return null;
        }
      })}

      {dryRun.choices
        .filter((choice) => !placedChoiceKeys.includes(choice.key))
        .map((choice) => (
          <ChoiceField
            key={choice.key}
            choice={choice}
            picked={choices[choice.key]}
            disabled={disabled}
            onChoose={onChoose}
            onChooseMany={onChooseMany}
            search={searches[choice.key] ?? ""}
            isSearchPending={isSearchPending}
            onSearch={onSearch}
          />
        ))}

      {afterChoices}

      {dryRun.display.map((block, n) =>
        block.kind === "figures" ? <Figures key={n} block={block} /> : null,
      )}

      {checks.length > 0 && (
        <div>
          <SectionTitle>Checks</SectionTitle>
          <ul className="space-y-1">
            {checks.map((check) => {
              const Icon = CHECK_ICON[check.state];
              return (
                <li key={check.label} className="flex gap-2 text-sm">
                  <Icon
                    className={cn(
                      "mt-0.5 size-4 shrink-0",
                      CHECK_TONE[check.state],
                    )}
                    aria-label={CHECK_WORD[check.state]}
                  />
                  <span>
                    {check.label}
                    {check.evidence && (
                      <span className="text-muted-foreground">
                        {" "}
                        · {check.evidence}
                      </span>
                    )}
                    {check.link && <> {refLink(check.link, "Open →")}</>}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {dryRun.rows && dryRun.rows.length > 0 && (
        <div>
          <SectionTitle>What it will do</SectionTitle>
          <ul className="space-y-0.5 text-sm">
            {dryRun.rows.map((row) => (
              <li key={row.target.id} className="flex gap-2">
                <span className="font-medium tabular-nums">
                  {row.label ?? row.target.id}
                </span>
                <span
                  className={
                    row.ok
                      ? "text-muted-foreground"
                      : "text-red-700 dark:text-red-400"
                  }
                >
                  {row.ok ? "Ready" : row.error}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {dryRun.confirmations.length > 0 && (
        <div className="space-y-2">
          {dryRun.confirmations.map((confirmation) => {
            const id = `confirm-${confirmation.key}`.replace(
              /[^a-zA-Z0-9_-]/g,
              "-",
            );
            return (
              <div key={confirmation.key} className="flex items-start gap-2">
                <Checkbox
                  id={id}
                  checked={confirmed.has(confirmation.key)}
                  disabled={disabled}
                  onCheckedChange={(checked) =>
                    onConfirm(confirmation.key, checked === true)
                  }
                  className="mt-0.5"
                />
                <Label htmlFor={id} className="font-medium">
                  {confirmation.label}
                </Label>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * One choice from the dry run: radio cards, or several to pick. Drawn with the
 * dry run's other blocks, or by the shell in its field's place when its key is
 * a declared request field (Set delivery price…'s delivery comes first).
 */
export function ChoiceField({
  choice,
  picked,
  disabled,
  onChoose,
  onChooseMany,
  search = "",
  isSearchPending = false,
  onSearch,
}: {
  choice: Choice;
  picked: ChoiceValue | undefined;
  disabled: boolean;
  onChoose: (key: string, value: string) => void;
  onChooseMany: (key: string, values: string[]) => void;
  /** A searchable choice's typed text, whether the owner is still answering it, and where a change goes. */
  search?: string;
  isSearchPending?: boolean;
  onSearch?: (key: string, text: string) => void;
}) {
  const remoteSearch =
    choice.searchable && onSearch
      ? {
          text: search,
          isPending: isSearchPending,
          onChange: (text: string) => onSearch(choice.key, text),
        }
      : undefined;
  return choice.multiple ? (
    <div>
      <SectionTitle>{choice.label}</SectionTitle>
      <MultiSelect
        id={`choice-${choice.key}`}
        label={choice.label}
        options={choice.options}
        value={Array.isArray(picked) ? picked : []}
        disabled={disabled}
        variant="cards"
        remoteSearch={remoteSearch}
        onChange={(values) => onChooseMany(choice.key, values)}
      />
    </div>
  ) : (
    <SingleChoice
      choice={choice}
      picked={picked}
      disabled={disabled}
      onChoose={onChoose}
    />
  );
}

/** One pick from radio cards; a long list (Convert lead's CSRs) has a search box that narrows the cards (`useChoiceSearch`). */
function SingleChoice({
  choice,
  picked,
  disabled,
  onChoose,
}: {
  choice: Choice;
  picked: ChoiceValue | undefined;
  disabled: boolean;
  onChoose: (key: string, value: string) => void;
}) {
  // The option already picked stays on screen whatever is typed, so narrowing never hides the answer.
  const { shown, searchBox } = useChoiceSearch({
    label: choice.label,
    options: choice.options,
    disabled,
    isKept: (option) => option.value === picked,
  });
  return (
    <div>
      <SectionTitle>{choice.label}</SectionTitle>
      {searchBox}
      <RadioGroup
        aria-label={choice.label}
        value={typeof picked === "string" ? picked : ""}
        disabled={disabled}
        onValueChange={(value) => onChoose(choice.key, value)}
        className="gap-1.5"
      >
        {shown.map((option) => {
          const id = `choice-${choice.key}-${option.value}`.replace(
            /[^a-zA-Z0-9_-]/g,
            "-",
          );
          const isChosen = picked === option.value;
          return (
            <Label
              key={option.value}
              htmlFor={id}
              className={cn(
                "flex cursor-pointer items-start gap-3 rounded-md border border-border px-3 py-2 font-normal",
                isChosen && "border-primary bg-primary/5",
                option.disabled_reason && "cursor-not-allowed opacity-60",
              )}
            >
              <RadioGroupItem
                id={id}
                value={option.value}
                disabled={!!option.disabled_reason}
                className="mt-0.5"
              />
              <span className="flex min-w-0 flex-col">
                <span className="flex flex-wrap items-center gap-2">
                  {option.label}
                  <OptionTag tag={option.tag} />
                </span>
                {(option.disabled_reason ?? option.why) && (
                  <span className="text-xs text-muted-foreground">
                    {option.disabled_reason ?? option.why}
                  </span>
                )}
              </span>
              {option.facts && (
                <span className="ml-auto text-right text-sm tabular-nums sm:whitespace-nowrap">
                  {option.facts}
                </span>
              )}
            </Label>
          );
        })}
      </RadioGroup>
    </div>
  );
}

const FIGURES_TONE: Record<FiguresBlock["tone"], string> = {
  ok: "border-green-600 bg-green-50 text-green-900 dark:bg-green-950/30 dark:text-green-200",
  warn: "border-amber-600 bg-amber-50 text-amber-900 dark:bg-amber-950/30 dark:text-amber-200",
  fail: "border-red-600 bg-red-50 text-red-900 dark:bg-red-950/30 dark:text-red-200",
  neutral: "border-border bg-muted/50 text-foreground",
};

/** A few figures and the verdict they add up to, toned; what the change will do follows as plain lines. */
function Figures({ block }: { block: FiguresBlock }) {
  return (
    <div className="space-y-1.5 text-sm">
      <p className="tabular-nums text-muted-foreground">
        {block.figures.map(([label, value]) => `${label} ${value}`).join(" · ")}
      </p>
      <p
        role="status"
        className={cn(
          "rounded-md border-l-4 px-3 py-2 font-medium",
          FIGURES_TONE[block.tone],
        )}
      >
        {block.verdict}
      </p>
      {block.lines.map((line) => (
        <p key={line}>{line}</p>
      ))}
    </div>
  );
}
